import {
    BaseReservation,
    MovedInReservation,
} from '@r6digital/rapidstor-types/types/Reservation';

export type RestorableReservation = BaseReservation | MovedInReservation;

const isFinalized = (reservation: RestorableReservation): boolean => 'finalized' in reservation && reservation.finalized;

/**
 * A paid ("Pay later") reservation is created as an enquiry pending payment and
 * stays that way until the reservation fee is collected on the
 * reservation-with-fee screen (`reservationFeeCharged` flips to `true`).
 *
 * Restoring one of these back into the booking flow hands the user a valid
 * reservation token without the fee ever being paid, which lets them re-submit
 * and reach a reserved/move-in state — bypassing the reservation-with-fee
 * screen entirely. These must therefore be treated as non-restorable.
 *
 * Free reservations (`enquiry === false`) and the Pay Now / move-in flow (also
 * created with `enquiry === false`) are unaffected, as are paid reservations
 * whose fee has already been charged.
 */
const isFeePendingReservation = (reservation: RestorableReservation): boolean => reservation.enquiry === true && !reservation.reservationFeeCharged;

/**
 * Selects the reservation to restore for a returning session, or `null` when
 * none is restorable. Excludes finalized (moved-in) reservations and
 * fee-pending reservations (see {@link isFeePendingReservation}).
 */
export function selectReservationToRestore(
    reservations: RestorableReservation[],
): RestorableReservation | null {
    const restorable = reservations.filter(
        (reservation) => !isFinalized(reservation) && !isFeePendingReservation(reservation),
    );
    return restorable[restorable.length - 1] ?? null;
}
